import React, { useEffect, useState } from "react";
import {
  Button,
  Table,
  Tag,
  Row,
  Col,
  Card,
  Dropdown,
  Menu,
  Space,
  message,
  Tooltip,
  Modal,
  List,
  Avatar,
  Switch,
  Spin,
} from "antd";

import {
  EllipsisOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClearOutlined,
  WalletOutlined,
  LockOutlined,
  UnlockOutlined,
} from "@ant-design/icons";
import { filterUsers } from "../../api/UserAPI.js";
import UserWalletDetailsModal from "./UserWalletDetailsModal.jsx";
import { FreezeWallet, toggleQrType } from "../../api/WalletApi.js";
import AdjustWalletModal from "./AdjustWalletModal.jsx";
import { AdjustWalletTransaction } from "../../api/WalletApi.js";
import AuditTrailModal from "./AuditTrailModal.jsx";
import { formatIdWithPrefix, maskString } from "../../Custom/Custom.js";
import FindUserModal from "../UserManagmentPackage/FindUserModal.jsx";
import { SearchIcon } from "lucide-react";

// ─── Badge Helpers ────────────────────────────────────────────────────────────

const getStatusBadge = (status) => {
  const label = status === 1 || status === "Active" ? "Active" : "Suspended";
  const color = label === "Active" ? "green" : "red";
  return (
    <Tag color={color} style={{ borderRadius: 12, fontWeight: 500 }}>
      {label}
    </Tag>
  );
};

const getKycBadge = (kyc) => {
  const label =
    kyc === "PENDING"
      ? "Pending"
      : kyc === "APPROVED"
      ? "Approved"
      : kyc === "REJECTED"
      ? "Rejected"
      : "Under Review";
  const color =
    label === "Approved"
      ? "green"
      : label === "Pending"
      ? "gold"
      : label === "Rejected"
      ? "red"
      : "default";
  return (
    <Tag color={color} style={{ borderRadius: 12, fontWeight: 500 }}>
      {label}
    </Tag>
  );
};

const getWalletActiveBadge = (wallet) => {
  if (!wallet) return null;
  const isActive = wallet.walletActive === true;
  const color = isActive ? "green" : "red";
  const text = isActive ? "Active" : "Frozen";
  const icon = isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />;
  return (
    <Tooltip title={`Wallet is ${text}`}>
      <Tag color={color} icon={icon} style={{ borderRadius: 12, fontWeight: 500 }}>
        {text}
      </Tag>
    </Tooltip>
  );
};

// ─── Wallet List Modal ────────────────────────────────────────────────────────

function WalletListModal({ visible, user, onClose, onFreezeToggle, freezeLoadingId }) {
  if (!user) return null;

  const wallets = user.wallets || [];

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      title={
        <Space>
          <WalletOutlined style={{ color: "#a00030" }} />
          <span style={{ fontWeight: 600 }}>
            Wallets — {user.name}
          </span>
        </Space>
      }
      width={520}
    >
      {wallets.length === 0 ? (
        <div style={{ textAlign: "center", padding: "32px 0", color: "#888" }}>
          No wallets found for this user.
        </div>
      ) : (
        <List
          dataSource={wallets}
          renderItem={(wallet) => {
            const isActive = wallet.walletActive === true;
            const isLoading = freezeLoadingId === wallet.id;

            return (
              <List.Item
                key={wallet.id}
                style={{
                  border: "1px solid #f0f0f0",
                  borderRadius: 10,
                  marginBottom: 12,
                  padding: "14px 18px",
                  background: "#fafafa",
                }}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar
                      style={{
                        background: isActive ? "#f6ffed" : "#fff1f0",
                        border: `1px solid ${isActive ? "#b7eb8f" : "#ffa39e"}`,
                      }}
                      icon={
                        isActive ? (
                          <UnlockOutlined style={{ color: "#52c41a" }} />
                        ) : (
                          <LockOutlined style={{ color: "#ff4d4f" }} />
                        )
                      }
                    />
                  }
                  title={
                    <Space>
                      <span style={{ fontWeight: 600 }}>
                        {wallet.walletName || `Wallet #${wallet.id}`}
                      </span>
                      {wallet.primaryWallet && (
                        <Tag color="blue" style={{ borderRadius: 8, fontSize: 11 }}>
                          Primary
                        </Tag>
                      )}
                      {wallet.staticQrCode ? (
                        <Tag color="purple" style={{ borderRadius: 8, fontSize: 11 }}>
                          Static QR
                        </Tag>
                      ) : (
                        <Tag color="cyan" style={{ borderRadius: 8, fontSize: 11 }}>
                          Dynamic QR
                        </Tag>
                      )}
                    </Space>
                  }
                  description={
                    <span style={{ color: "#555", fontSize: 13 }}>
                      Balance:{" "}
                      <strong>
                        ${wallet.totalBalance?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </strong>
                    </span>
                  }
                />

                <Space direction="vertical" align="center" size={4}>
                  <Tag
                    color={isActive ? "green" : "red"}
                    icon={isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                    style={{ borderRadius: 10, fontWeight: 500 }}
                  >
                    {isActive ? "Active" : "Frozen"}
                  </Tag>
                  {isLoading ? (
                    <Spin size="small" />
                  ) : (
                    <Switch
                      checked={isActive}
                      checkedChildren="Active"
                      unCheckedChildren="Frozen"
                      onChange={() => onFreezeToggle(wallet)}
                      style={{
                        background: isActive ? "#52c41a" : "#ff4d4f",
                      }}
                    />
                  )}
                </Space>
              </List.Item>
            );
          }}
        />
      )}
    </Modal>
  );
}

// ─── Action Menu ──────────────────────────────────────────────────────────────

function ActionMenu({
  user,
  onViewDetails,
  onAdjustBalance,
  onViewWallets,
  onViewAuditTrail,
  onToggleQr,
}) {
  const menu = (
    <Menu>
      <Menu.Item onClick={() => onViewDetails(user)}>View Wallet Details</Menu.Item>
      <Menu.Item onClick={() => onAdjustBalance(user)}>Adjust Balance</Menu.Item>
      <Menu.Item onClick={() => onViewWallets(user)}>Manage Wallets</Menu.Item>
      <Menu.Item onClick={() => onToggleQr(user)}>
        {user.wallet?.staticQrCode ? "Switch to DYNAMIC QR" : "Switch to STATIC QR"}
      </Menu.Item>
      <Menu.Item onClick={() => onViewAuditTrail(user)}>View Audit Trail</Menu.Item>
    </Menu>
  );
  return (
    <Dropdown overlay={menu} trigger={["click"]}>
      <Button type="text" icon={<EllipsisOutlined style={{ fontSize: 20 }} />} />
    </Dropdown>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function WalletOperations() {
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    totalItems: 0,
    currentPage: 0,
    pageSize: 10,
  });
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({ search: "" });
  const [isAdjustModalOpen, setIsAdjustModalOpen] = useState(false);
  const [adjustUser, setAdjustUser] = useState(null);
  const [isAuditModalOpen, setIsAuditModalOpen] = useState(false);
  const [auditUser, setAuditUser] = useState(null);
  const [isFindUserModalOpen, setIsFindUserModalOpen] = useState(false);
  const [isMasked, setIsMasked] = useState(true);

  // Details modal
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Wallet list modal
  const [isWalletModalOpen, setIsWalletModalOpen] = useState(false);
  const [walletModalUser, setWalletModalUser] = useState(null);
  const [freezeLoadingId, setFreezeLoadingId] = useState(null);

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchWalletUsers = (page = 0, size = 10, filterData = {}) => {
    setLoading(true);
    filterUsers({ pageNo: page, size, ...filterData })
      .then((res) => {
        const { users, totalItems, currentPage } = res.data.data || {};
        const walletRows = (users || []).map((u) => ({
          id: u.id,
          name: u.name,
          email: u.email,
          mobileNo: u.mobileNo,
          userStatus: u.userStatus,
          kycStatus: u?.kyc?.status,
          accountNumber: u.accountNumber,
          dob: u.dob,
          nationality: u.nationality,
          gender: u.gender,
          refferalCode: u.refferalCode,
          userType: u.userType,
          updatedDate: u.updatedDate,
          welcomeBonusClaimed: u.welcomeBonusClaimed,
          // Keep raw wallets array for the modal
          wallets: u.wallets || [],
          // Primary wallet for table display
          wallet: {
            id: u.wallets?.[0]?.id,
            totalBalance: u.wallets?.[0]?.totalBalance ?? 0,
            status: u.wallets?.[0]?.walletActive ? "Active" : "Frozen",
            lastUpdated: u.wallets?.[0]?.updatedDate,
            createdDate: u.wallets?.[0]?.createdDate,
            updatedDate: u.wallets?.[0]?.updatedDate,
            walletActive: u.wallets?.[0]?.walletActive,
            staticQrCode: u.wallets?.[0]?.staticQrCode,
          },
          createdDate: u.createdDate,
          userCode: u.userCode,
        }));
        setData(walletRows);
        setPagination({
          totalItems: totalItems || 0,
          currentPage: currentPage || 0,
          pageSize: size,
        });
      })
      .catch((error) => console.log("error", error))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchWalletUsers(0, pagination.pageSize, filters);
  }, []);

  // ── Wallet Modal ───────────────────────────────────────────────────────────

  const openWalletModal = (user) => {
    setWalletModalUser(user);
    setIsWalletModalOpen(true);
  };

  const closeWalletModal = () => {
    setWalletModalUser(null);
    setIsWalletModalOpen(false);
  };

  const handleFreezeToggle = async (wallet) => {
    try {
      setFreezeLoadingId(wallet.id);
      const payload = { id: wallet.id };
      await FreezeWallet(payload);

      const action = wallet.walletActive ? "frozen" : "activated";
      message.success(`Wallet #${wallet.id} has been ${action}`);

      // Refresh data
      await fetchWalletUsers(pagination.currentPage, pagination.pageSize, filters);

      // Update walletModalUser with fresh data so modal reflects change
      setWalletModalUser((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          wallets: prev.wallets.map((w) =>
            w.id === wallet.id ? { ...w, walletActive: !w.walletActive } : w
          ),
        };
      });
    } catch (error) {
      message.error("Failed to toggle wallet freeze status");
    } finally {
      setFreezeLoadingId(null);
    }
  };

  // ── Other Handlers ─────────────────────────────────────────────────────────

  const handleToggleQrType = async (user) => {
    try {
      setLoading(true);
      const payload = { userId: user.id };
      const res = await toggleQrType(payload);
      const isStatic = res.data?.data;
      const newQrType = isStatic ? "STATIC" : "DYNAMIC";
      message.success(`QR type changed to ${newQrType} for ${user.name}`);
      fetchWalletUsers(pagination.currentPage, pagination.pageSize, filters);
      setIsMasked(true);
    } catch (error) {
      message.error("Failed to toggle QR type");
    } finally {
      setLoading(false);
    }
  };

  const handleWalletAdjustment = async (payload) => {
    try {
      setLoading(true);
      await AdjustWalletTransaction(payload);
      message.success("Wallet adjusted successfully");
      setIsAdjustModalOpen(false);
      setAdjustUser(null);
      fetchWalletUsers(pagination.currentPage, pagination.pageSize, filters);
    } catch (error) {
      message.error("Failed to adjust wallet");
    } finally {
      setLoading(false);
    }
  };

  const openDetailsModal = (user) => {
    setSelectedUser(user);
    setIsDetailsModalOpen(true);
  };

  const closeDetailsModal = () => {
    setSelectedUser(null);
    setIsDetailsModalOpen(false);
  };

  const openAdjustModal = (user) => {
    setAdjustUser(user);
    setIsAdjustModalOpen(true);
  };

  const openAuditTrailModal = (user) => {
    setAuditUser(user);
    setIsAuditModalOpen(true);
  };

  const closeAuditTrailModal = () => {
    setAuditUser(null);
    setIsAuditModalOpen(false);
  };

  const clearFilter = () => {
    setIsMasked(true);
    fetchWalletUsers();
  };

  const handleFindUser = (filtersData) => {
    setIsFindUserModalOpen(false);
    fetchWalletUsers(0, pagination.pageSize, filtersData);
    setIsMasked(false);
  };

  // ── Columns ────────────────────────────────────────────────────────────────

  const columns = [
    {
      title: "User",
      dataIndex: "name",
      key: "name",
      render: (val, row) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: 500 }}>
            {isMasked ? maskString(val, 1, 0) : val}
          </span>
          <span style={{ color: "#888", fontSize: 12 }}>
            {formatIdWithPrefix(row.userCode || row.id, "WLT")}
          </span>
        </Space>
      ),
    },
    {
      title: "Contact",
      key: "contact",
      render: (record) => (
        <>
          <div>{isMasked ? maskString(record.email, 2, 10) : record.email}</div>
          <div style={{ color: "#888" }}>
            {isMasked ? maskString(record.mobileNo, 2, 2) : record.mobileNo}
          </div>
        </>
      ),
    },
    {
      title: "Status",
      dataIndex: "userStatus",
      key: "status",
      render: getStatusBadge,
    },
    {
      title: "KYC Status",
      dataIndex: "kycStatus",
      key: "kycStatus",
      render: getKycBadge,
    },
    {
      title: "Wallet Status",
      key: "walletStatus",
      render: (_, record) => getWalletActiveBadge(record.wallet),
    },
    {
      title: "Wallet Balance",
      dataIndex: ["wallet", "totalBalance"],
      key: "walletBalance",
      render: (balance) =>
        typeof balance === "number"
          ? isMasked
            ? maskString(
                `$${balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
                0,
                0
              )
            : `$${balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
          : "$0.00",
    },
    {
      title: "Join Date",
      dataIndex: "createdDate",
      key: "createdDate",
      render: (date) => (date ? date.slice(0, 10) : ""),
    },
  ];

  if (!isMasked) {
    columns.push({
      title: "",
      key: "action",
      render: (_, user) => (
        <ActionMenu
          user={user}
          onViewDetails={openDetailsModal}
          onAdjustBalance={openAdjustModal}
          onViewWallets={openWalletModal}        // ← new
          onViewAuditTrail={openAuditTrailModal}
          onToggleQr={handleToggleQrType}
        />
      ),
    });
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <FindUserModal
        open={isFindUserModalOpen}
        onCancel={() => setIsFindUserModalOpen(false)}
        onFind={handleFindUser}
      />

      <div style={{ background: "#fff", minHeight: "100vh", padding: 24 }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <div style={{ fontSize: 28, fontWeight: 600 }}>Wallet Operations</div>
            <div style={{ color: "#555" }}>Manage wallet balances, status, and audit logs</div>
          </Col>
          <Col>
            <Space>
              <Button type="primary" icon={<ClearOutlined />} onClick={clearFilter}>
                Clear
              </Button>
              <Button
                type="primary"
                icon={<SearchIcon />}
                onClick={() => setIsFindUserModalOpen(true)}
              >
                Find User
              </Button>
            </Space>
          </Col>
        </Row>

        <Card bodyStyle={{ padding: "16px 24px" }}>
          <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
            <Col>
              <h2>All Wallets</h2>
            </Col>
          </Row>

          <Table
            columns={columns}
            dataSource={data}
            loading={loading}
            rowKey="id"
            pagination={{
              current: pagination.currentPage + 1,
              pageSize: pagination.pageSize,
              total: pagination.totalItems,
              onChange: (page) =>
                fetchWalletUsers(page - 1, pagination.pageSize, filters),
            }}
            style={{ borderRadius: 12 }}
          />
        </Card>

        {/* Wallet Details Modal */}
        <UserWalletDetailsModal
          visible={isDetailsModalOpen}
          onClose={closeDetailsModal}
          user={selectedUser}
        />

        {/* Wallet List + Freeze/Unfreeze Modal */}
        <WalletListModal
          visible={isWalletModalOpen}
          user={walletModalUser}
          onClose={closeWalletModal}
          onFreezeToggle={handleFreezeToggle}
          freezeLoadingId={freezeLoadingId}
        />

        {/* Adjust Wallet Modal */}
        {isAdjustModalOpen && adjustUser && (
          <AdjustWalletModal
            user={adjustUser}
            onSubmit={handleWalletAdjustment}
            onClose={() => {
              setIsAdjustModalOpen(false);
              setAdjustUser(null);
            }}
          />
        )}

        {/* Audit Trail Modal */}
        {isAuditModalOpen && auditUser && (
          <AuditTrailModal
            visible={isAuditModalOpen}
            onClose={closeAuditTrailModal}
            user={auditUser}
          />
        )}
      </div>
    </>
  );
}